<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useWoowCrm, customerLabel } from 'dashboard/composables/useWoowCrm';
import WoowCrmAPI from 'dashboard/api/integrations/woowCrm';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const conversationId = computed(() => props.conversationId);
const { linkedCustomer } = useWoowCrm(conversationId);

const dialogRef = ref(null);
const isSaving = ref(false);

// Values mirror what StoreLeadData validates on the WOOW side.
const serviceOptions = computed(() => [
  { value: 'us2bd', label: t('WOOW_CRM.LEAD.SERVICE_TYPES.US2BD') },
  { value: 'bd2us', label: t('WOOW_CRM.LEAD.SERVICE_TYPES.BD2US') },
  { value: 'bd2any', label: t('WOOW_CRM.LEAD.SERVICE_TYPES.BD2ANY') },
  { value: 'shopping', label: t('WOOW_CRM.LEAD.SERVICE_TYPES.SHOPPING') },
  { value: 'us2any', label: t('WOOW_CRM.LEAD.SERVICE_TYPES.US2ANY') },
]);

const priorityOptions = computed(() => [
  { value: 'high', label: t('WOOW_CRM.LEAD.PRIORITIES.HIGH') },
  { value: 'medium', label: t('WOOW_CRM.LEAD.PRIORITIES.MEDIUM') },
  { value: 'low', label: t('WOOW_CRM.LEAD.PRIORITIES.LOW') },
]);

const form = ref({
  service_type: null,
  priority: 'medium',
  est_weight: '',
  est_value: '',
  follow_up_at: '',
  notes: '',
});

const handleSubmit = async () => {
  isSaving.value = true;
  try {
    const { data } = await WoowCrmAPI.createLead(props.conversationId, {
      ...form.value,
      est_weight: form.value.est_weight || null,
      est_value: form.value.est_value || null,
      follow_up_at: form.value.follow_up_at || null,
    });
    useAlert(t('WOOW_CRM.LEAD.CREATED', { id: data.id }));
    dialogRef.value.close();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('WOOW_CRM.LEAD.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const open = () => {
  form.value = {
    service_type: null,
    priority: 'medium',
    est_weight: '',
    est_value: '',
    follow_up_at: '',
    notes: '',
  };
  dialogRef.value.open();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('WOOW_CRM.LEAD.TITLE')"
    :description="t('WOOW_CRM.LEAD.DESCRIPTION')"
    :confirm-button-label="t('WOOW_CRM.LEAD.SUBMIT')"
    :is-loading="isSaving"
    width="xl"
    @confirm="handleSubmit"
  >
    <div class="flex flex-col gap-4">
      <p v-if="linkedCustomer" class="text-xs text-n-slate-11">
        {{
          t('WOOW_CRM.LEAD.FOR_CUSTOMER', {
            name: customerLabel(linkedCustomer),
          })
        }}
      </p>
      <p v-else class="text-xs text-n-amber-11">
        {{ t('WOOW_CRM.LEAD.NO_CUSTOMER') }}
      </p>

      <div class="grid grid-cols-2 gap-4">
        <Select
          v-model="form.service_type"
          :options="serviceOptions"
          :label="t('WOOW_CRM.LEAD.FIELDS.SERVICE_TYPE')"
          :placeholder="t('WOOW_CRM.LEAD.FIELDS.SERVICE_TYPE_PLACEHOLDER')"
        />
        <Select
          v-model="form.priority"
          :options="priorityOptions"
          :label="t('WOOW_CRM.LEAD.FIELDS.PRIORITY')"
        />
        <Input
          v-model="form.est_weight"
          type="number"
          :label="t('WOOW_CRM.LEAD.FIELDS.EST_WEIGHT')"
        />
        <Input
          v-model="form.est_value"
          type="number"
          :label="t('WOOW_CRM.LEAD.FIELDS.EST_VALUE')"
        />
        <Input
          v-model="form.follow_up_at"
          type="date"
          :label="t('WOOW_CRM.LEAD.FIELDS.FOLLOW_UP_AT')"
        />
      </div>

      <TextArea
        v-model="form.notes"
        :label="t('WOOW_CRM.LEAD.FIELDS.NOTES')"
        :max-length="1000"
        auto-height
      />
    </div>
  </Dialog>
</template>
